import { useCallback, useEffect, useState } from "react";
import { Copy, KeyRound, Lock, Plus, Trash2, Unlock, UserPlus, Users, X } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription } from "@/components/ui/alert-dialog";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { copyText } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { useHub } from "@/components/AppShell";
import { EmptyState, Loader, PageHeader, StatusPill } from "@/components/common";
import { AdultAvatar, KidAvatar } from "@/components/Avatars";
import { InviteForm, KidForm } from "@/components/members/MemberForms";
import KidContactRules from "@/components/connect/KidContactRules";
import { Confirm } from "@/components/kids/kidsUi";
import { TransferButton, TransferInvite } from "@/components/family/Transfer";

const CYAN = "var(--glow-family)";
const ROLE_RGB = { owner: "var(--glow-home)", coparent: CYAN, relative: "var(--glow-connect)", kid: "var(--glow-kids)" };
const SECTIONS = [["voice", "Voice", "var(--glow-family)"], ["shutoffs", "Shutoffs", "var(--glow-home)"], ["prep", "Emergency prep", "var(--glow-home)"], ["away", "Away checklist", "var(--glow-home)"], ["manual", "Household manual", "var(--glow-home)"], ["lease", "Lease & rent", "var(--glow-home)"], ["movelog", "Move-in/out log", "var(--glow-home)"], ["maintenance", "Maintenance", "var(--glow-home)"], ["housing", "Housing costs", "var(--glow-home)"], ["utilities", "Utilities", "var(--glow-home)"], ["money", "Money", "var(--glow-money)"], ["kids", "Kids Corner", "var(--glow-kids)"], ["calendar", "Calendar", "var(--glow-calendar)"], ["connect", "Connect", "var(--glow-connect)"], ["media", "Media", "var(--glow-media)"], ["lists", "Lists", "var(--glow-lists)"], ["meals", "Meals", "var(--glow-lists)"], ["emergency", "Emergency info", "var(--glow-family)"], ["home", "Home care", "var(--glow-home)"], ["checkins", "Who's where", "var(--glow-connect)"], ["memories", "Memories", "var(--glow-home)"], ["documents", "Documents (open categories)", "var(--glow-family)"], ["inventory", "Inventory", "var(--glow-home)"], ["insurance", "Insurance", "var(--glow-home)"], ["vehicles", "Vehicles", "var(--glow-home)"], ["pets", "Pets", "var(--glow-home)"]];

function AccessToggles({ m, hubId, onChange }) {
  const flip = async (k, v) => {
    try {
      await api.patch(`/organizations/${hubId}/members/${m.user_id}/access`, { access: { [k]: v } });
      onChange();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-2" data-testid={`access-toggles-${m.user_id}`}>
      {SECTIONS.map(([k, label, rgb]) => (
        <label key={k} className="flex items-center gap-2 text-xs font-semibold" style={{ color: m.access[k] ? `rgb(${rgb})` : "rgb(var(--q-faint))" }}>
          <Switch checked={!!m.access[k]} onCheckedChange={(v) => flip(k, v)} data-testid={`access-toggle-${m.user_id}-${k}`} />
          {label}
        </label>
      ))}
    </div>
  );
}

function Section({ title, children, action }) {
  return (
    <section className="mb-8 q-rise">
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-[10px] font-black uppercase tracking-[0.18em]" style={{ color: `rgb(${CYAN})` }}>{title}</h2>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function Members() {
  const { hub, reloadHub } = useHub();
  const { session } = useAuth();
  const [data, setData] = useState(null);
  const [invites, setInvites] = useState([]);
  const [dialog, setDialog] = useState(null);
  const [delKid, setDelKid] = useState(null);
  const [removing, setRemoving] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/organizations/${hub.id}/members`);
    setData(data);
    if (data.can_manage) setInvites((await api.get(`/organizations/${hub.id}/invites`)).data);
  }, [hub.id]);
  useEffect(() => { load(); }, [load]);

  if (!data) return <Loader />;
  const manage = data.can_manage;
  const me = session?.user?.id;

  const remove = async () => {
    try {
      await api.delete(`/organizations/${hub.id}/members/${removing.user_id}`);
      toast.success(`${removing.name} was removed`);
      load();
    } catch (e) { toast.error(errMsg(e), { id: "remove-error" }); }
    setRemoving(null);
  };
  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="members-page">
      <PageHeader eyebrow="Family" title="Members" rgb={CYAN} testid="members-title" actions={manage && <>
        <button className="q-btn q-btn-secondary" onClick={() => setDialog({ type: "kid" })} data-testid="members-add-kid-btn"><Plus className="h-4 w-4" /> Add kid</button>
        <button className="q-btn q-btn-primary" onClick={() => setDialog({ type: "invite" })} data-testid="members-invite-btn"><UserPlus className="h-4 w-4" /> Invite</button>
      </>} />

      <TransferInvite hub={hub} onDone={() => { load(); reloadHub?.(); }} />
      <Section title="Grown-ups">
        <ul className="divide-y divide-q-border overflow-hidden rounded-xl border border-q-border bg-q-surface">
          {data.adults.map((m) => (
            <li key={m.user_id} className="px-4 py-4 sm:px-5" data-testid={`member-row-${m.user_id}`}>
              <div className="flex items-center gap-3">
                <AdultAvatar name={m.name} picture={m.picture} />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-semibold text-q-text" data-testid="member-name">{m.name}{m.user_id === me && <span className="ml-1.5 text-xs text-q-faint">(you)</span>}</div>
                  {m.email && <div className="truncate text-xs text-q-faint">{m.email}</div>}
                </div>
                <StatusPill label={m.role_label} rgb={ROLE_RGB[m.role]} testid={`member-role-${m.user_id}`} />
                {hub.my_role === "owner" && m.role === "coparent" && !hub.read_only && <TransferButton hub={hub} m={m} />}
                {manage && m.user_id !== me && (
                  <button className="q-btn q-btn-ghost px-2 py-1.5 hover:text-red-300" title="Remove from hub" onClick={() => setRemoving(m)} data-testid={`member-remove-btn-${m.user_id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                )}
              </div>
              {m.role === "relative" && manage && <div className="mt-3 pl-[52px]"><AccessToggles m={m} hubId={hub.id} onChange={load} /></div>}
            </li>
          ))}
        </ul>
      </Section>

      <Section title="Kids">
        {data.kids.length === 0 ? (
          <EmptyState Icon={Users} rgb="var(--glow-kids)" testid="kids-empty" title="No kids yet" hint="Add a kid with an avatar and a 4-digit PIN so they can sign in on their own."
            action={manage && <button className="q-btn q-btn-primary" onClick={() => setDialog({ type: "kid" })}>Add kid</button>} />
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {data.kids.map((k) => (
              <li key={k.id} className="flex items-center gap-3 rounded-2xl border bg-q-surface p-4" style={{ borderColor: "rgba(var(--glow-kids),0.25)" }} data-testid={`kid-row-${k.id}`}>
                <KidAvatar avatar={k.avatar} size={48} />
                <div className="min-w-0 flex-1">
                  <div className="truncate font-bold text-q-text" data-testid="kid-name">{k.name}</div>
                  <div className="mt-1 flex gap-1.5">
                    <StatusPill label="Kid" rgb="var(--glow-kids)" testid={`member-role-${k.id}`} />
                    {k.locked && <StatusPill label="Locked" rgb="var(--glow-media)" testid={`kid-locked-${k.id}`} />}
                  </div>
                </div>
                {manage && (
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    <label className="flex items-center gap-2 text-xs font-semibold" style={{ color: k.lists_access ? "rgb(var(--glow-lists))" : "rgb(var(--q-faint))" }}>
                      <Switch checked={!!k.lists_access} onCheckedChange={(v) => act(() => api.patch(`/organizations/${hub.id}/kids/${k.id}/lists-access`, { enabled: v }), v ? `${k.name} can use Lists` : `Lists is off for ${k.name}`)} data-testid={`kid-lists-toggle-${k.id}`} />
                      Lists
                    </label>
                    <label className="flex items-center gap-2 text-xs font-semibold text-q-muted">Stories/day
                      <select className="q-input min-h-[44px] w-20" value={k.story_limit ?? 3} onChange={(e) => act(() => api.patch(`/organizations/${hub.id}/kids/${k.id}/story-limit`, { limit: Number(e.target.value) }), `${k.name} can make ${e.target.value} stories a day`)} data-testid={`kid-story-limit-${k.id}`}>
                        {Array.from({ length: 11 }, (_, n) => <option key={n} value={n}>{n}</option>)}
                      </select>
                    </label>
                    {[["voice", "Voice"], ["meals", "Meals"], ["emergency", "Emergency info"], ["checkins", "Who's where"], ["memories", "Memories"], ["pets", "Pets"], ["health", "Health"], ["school", "School"], ["birthdays", "Birthdays"]].map(([sec, label]) => (
                      <label key={sec} className="flex items-center gap-2 text-xs font-semibold" style={{ color: k[`${sec}_access`] ? "rgb(var(--glow-family))" : "rgb(var(--q-faint))" }}>
                        <Switch checked={!!k[`${sec}_access`]} onCheckedChange={(v) => act(() => api.patch(`/organizations/${hub.id}/kids/${k.id}/section-access`, { section: sec, enabled: v }), v ? `${k.name} can use ${label}` : `${label} is off for ${k.name}`)} data-testid={`kid-${sec}-toggle-${k.id}`} />
                        {label}
                      </label>
                    ))}
                  </div>
                )}
                {manage && (
                  <div className="flex gap-1">
                    {k.locked && <button className="q-btn q-btn-ghost px-2 py-1.5" title="Unlock" onClick={() => act(() => api.post(`/organizations/${hub.id}/kids/${k.id}/unlock`), `${k.name} unlocked`)} data-testid={`kid-unlock-btn-${k.id}`}><Unlock className="h-4 w-4" /></button>}
                    <button className="q-btn q-btn-ghost px-2 py-1.5" title="Change PIN" onClick={() => setDialog({ type: "pin", kid: k })} data-testid={`kid-change-pin-btn-${k.id}`}><KeyRound className="h-4 w-4" /></button>
                    <button className="q-btn q-btn-ghost min-h-[44px] min-w-[44px] px-2 py-1.5 hover:text-red-300" title={`Delete ${k.name}'s profile`} aria-label={`Delete ${k.name}'s profile`} onClick={() => setDelKid(k)} data-testid={`kid-delete-btn-${k.id}`}><Trash2 className="h-4 w-4" /></button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}
      </Section>
      {hub.is_parent && hub.my_access?.connect && <KidContactRules hubId={hub.id} />}

      {manage && (
        <Section title="Pending invites">
          {invites.length === 0 ? (
            <EmptyState Icon={UserPlus} rgb={CYAN} testid="invites-empty" title="No pending invites" hint="Invite a co-parent or a relative with a link that works once for 7 days."
              action={<button className="q-btn q-btn-primary" onClick={() => setDialog({ type: "invite" })}>Invite someone</button>} />
          ) : (
            <ul className="divide-y divide-q-border overflow-hidden rounded-xl border border-q-border bg-q-surface">
              {invites.map((i) => (
                <li key={i.id} className="flex flex-wrap items-center gap-3 px-4 py-3 sm:px-5" data-testid={`invite-row-${i.id}`}>
                  <StatusPill label={i.role_label} rgb={ROLE_RGB[i.role]} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-semibold text-q-text" data-testid="invite-row-email">{i.email || "Link only"}</div>
                    <div className="text-xs text-q-faint">Expires {fmtDate(i.expires_at.slice(0, 10))}</div>
                  </div>
                  <button className="q-btn q-btn-ghost px-2.5 py-1.5 text-xs" onClick={async () => (await copyText(i.link)) && toast.success("Copied")} data-testid={`invite-copy-btn-${i.id}`}><Copy className="h-3.5 w-3.5" /> Copy link</button>
                  <button className="q-btn q-btn-ghost px-2.5 py-1.5 text-xs hover:text-red-300" onClick={() => act(() => api.delete(`/organizations/${hub.id}/invites/${i.id}`), "Invite revoked")} data-testid={`invite-revoke-btn-${i.id}`}><X className="h-3.5 w-3.5" /> Revoke</button>
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-h-[92vh] overflow-y-auto border-q-line bg-q-surface sm:max-w-md" data-testid="members-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-extrabold">
              {dialog?.type === "invite" ? "Invite to " + hub.name : dialog?.type === "kid" ? "Add a kid" : `Change ${dialog?.kid?.name}'s PIN`}
            </DialogTitle>
            <DialogDescription className="text-q-muted">
              {dialog?.type === "invite" ? "Share the link, or add an email and we'll send it." : dialog?.type === "kid" ? "Kids sign in with your family code, their avatar and this PIN." : "The new PIN works at their next sign-in."}
            </DialogDescription>
          </DialogHeader>
          {dialog?.type === "invite" && <InviteForm hubId={hub.id} onCreated={load} />}
          {dialog?.type === "kid" && <KidForm hubId={hub.id} onSaved={() => { toast.success("Kid added"); setDialog(null); load(); }} />}
          {dialog?.type === "pin" && <KidForm hubId={hub.id} kid={dialog.kid} onSaved={() => { toast.success("PIN changed"); setDialog(null); load(); }} />}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)}>
        <AlertDialogContent className="border-q-line bg-q-surface" data-testid="member-remove-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">Remove {removing?.name} from the hub?</AlertDialogTitle>
            <AlertDialogDescription className="text-q-muted">They lose access straight away.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="member-remove-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-red-600 text-white hover:bg-red-500" data-testid="member-remove-confirm-btn">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <Confirm open={!!delKid} onClose={() => setDelKid(null)} title={`Delete ${delKid?.name}'s profile?`} confirmLabel="Delete profile"
        body={`${delKid?.name}'s chores, stars, wish list and settings are removed for good. This can't be undone.`}
        onConfirm={() => act(() => api.delete(`/organizations/${hub.id}/kids/${delKid.id}`), `${delKid.name}'s profile was deleted`).then(() => setDelKid(null))} testid="kid-delete-confirm" />
      {!manage && <p className="mt-2 flex items-center gap-2 text-xs text-q-faint" data-testid="members-readonly-note"><Lock className="h-3 w-3" /> Only parents can invite, remove or change access.</p>}
    </div>
  );
}
